import type { HostingDeclaredSiteView } from '@tosho/contracts';
import { BusinessLogo } from './BusinessLogo';
import { PrototypeIcon } from './PrototypeIcon';
import { SITE_CMS, SITE_ENVIRONMENTS, siteCreationDate } from './hosting-site-profile';
export function HostingDeclaredSiteRow({
  site,
  organizationLabel,
  organizationLogoUrl,
  onEdit,
}: {
  readonly site: HostingDeclaredSiteView;
  readonly organizationLabel: string;
  readonly organizationLogoUrl: string | null;
  readonly onEdit: () => void;
}): React.JSX.Element {
  const cms = SITE_CMS[site.profile.cms];
  return (
    <tr>
      <td data-label="Site et Client">
        <strong>{site.asciiName}</strong>
        <span className={'hosting-sites__environment is-' + site.profile.environment.toLowerCase()}>
          {SITE_ENVIRONMENTS[site.profile.environment]}
        </span>
        <div className="hosting-sites__identity">
          <div>
            <small>{site.organizationSite ? 'Organisation' : 'Client'}</small>
            {site.organizationSite ? (
              <BusinessLogo name={organizationLabel} logoUrl={organizationLogoUrl} />
            ) : site.client ? (
              <BusinessLogo
                name={site.client.displayName}
                logoUrl={
                  site.client.hasLogo
                    ? `/api/clients/${encodeURIComponent(site.client.id)}/logo?v=${site.client.logoVersion}`
                    : null
                }
              />
            ) : (
              <small>À rattacher</small>
            )}
          </div>
          <div>
            <small>CMS</small>
            <div className="hosting-sites__cms">
              {cms.logo ? (
                <img src={cms.logo} alt={cms.name} />
              ) : (
                <span>{site.profile.cms === 'OTHER' ? site.profile.otherCms : cms.name}</span>
              )}
            </div>
          </div>
          <div className="hosting-sites__mobile-server">
            <small>Serveur</small>
            <span>
              <PrototypeIcon name="network" />
            </span>
            <strong>{site.serverLabel}</strong>
          </div>
          <p className="hosting-sites__mobile-owner">
            Propriétaire :{' '}
            {site.serverOwnerKind === 'CLIENT'
              ? (site.serverOwnerClient?.displayName ?? 'Client propriétaire')
              : organizationLabel}
          </p>
        </div>
      </td>
      <td data-label="Serveur">
        <strong>{site.serverLabel}</strong>
        <small>
          Propriétaire :{' '}
          {site.serverOwnerKind === 'CLIENT'
            ? site.serverOwnerClient?.displayName
            : organizationLabel}
        </small>
      </td>
      <td data-label="Dernière sauvegarde">
        <span className="hosting-declared-site__unknown">
          <PrototypeIcon name="history" />
          Non observée
        </span>
      </td>
      <td data-label="Stockage">
        <span className="hosting-declared-site__unknown">
          <PrototypeIcon name="database" />
          Non observé
        </span>
      </td>
      <td data-label="Vhost / domaine">
        <strong>Site déclaré</strong>
        <small>Hors inventaire Plesk</small>
        <small>
          {site.profile.createdOn
            ? 'Créé le ' + siteCreationDate(site.profile.createdOn) + ' · déclaré'
            : 'Création à renseigner'}
        </small>
      </td>
      <td data-label="État et dernière lecture">
        <span className="hosting-site-status__badge">
          <PrototypeIcon name="circle-help" />
          État non observé
        </span>
        <small>Référencé dans Tosho</small>
        <small>Modifié le {new Date(site.updatedAt).toLocaleString('fr-FR')}</small>
      </td>
      <td className="hosting-servers__actions" data-label="Actions">
        <div className="hosting-sites__actions">
          <a
            className="hosting-declared-site__link"
            href={site.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={'Ouvrir le site — ' + site.asciiName}
            title={'Ouvrir ' + site.url}
          >
            <PrototypeIcon name="arrow-right" />
          </a>
          <button
            type="button"
            className="hosting-servers__icon-button"
            id={'hosting-declared-site-' + site.id}
            aria-label={'Modifier le site déclaré — ' + site.asciiName}
            title="Modifier le site déclaré"
            onClick={onEdit}
          >
            <PrototypeIcon name="pencil" />
          </button>
        </div>
      </td>
    </tr>
  );
}
